import { type QuoteDocument } from '@kalos/kalos-rpc';
import { Badge, Card, CardContent, Skeleton } from '@kalos/ui';
import { format, parseISO } from 'date-fns';
import { getLineDiscount } from 'kalos-modules';
import { type FC, useMemo } from 'react';

import { NULL_TIME } from '../../../../constants';
import { usePropertiesGetQuery } from '../../../../hooks/react-query/usePropertiesQuery';
import { useQuoteLineQuery } from '../../../../hooks/react-query/useQuoteLineQuery';
import {
  getQuoteDocumentTaxCost,
  getQuoteDocumentTaxRate,
} from '../../../../utils/quoteDocumentPricing';
import { RowPill } from '../../../ComponentsLibrary/AdvancedSearch/SearchResultRow';
import { FieldTotals } from '../FieldTotals';
import { LineItemRow } from '../LineItemRow';

interface ProposalDetailsViewProps {
  proposal: QuoteDocument;
  /**
   * Phones and tablets, in a bottom sheet: one status line, the services as the record's line
   * rows and the record's totals, instead of a card per fact. The property is left out because
   * the sheet opens from that property's tab.
   */
  compact?: boolean;
}

export const ProposalDetailsView: FC<ProposalDetailsViewProps> = ({ proposal, compact }) => {
  // Fetch quote lines for this proposal
  const { data: quoteLinesData, isLoading: isLoadingLines } = useQuoteLineQuery({
    filters: {
      quoteDocumentId: proposal.id,
      isActive: 1,
      withoutLimit: true,
    },
    enabled: !!proposal.id,
  });

  // Fetch property for address info
  const { data: property } = usePropertiesGetQuery({
    filter: { id: proposal.propertyId || 0 },
    enabled: !!proposal.propertyId,
  });

  const quoteLines = useMemo(() => quoteLinesData?.results || [], [quoteLinesData?.results]);

  const discountPercentValue = parseFloat(proposal.discount || '0');
  const discountCostValue = parseFloat(proposal.discountcost || '0');

  // Calculate totals from quote document discount/tax
  const { subtotal, discountAmount, taxAmount, total } = useMemo(() => {
    const sub = quoteLines.reduce((sum, line) => {
      const adjustment = line.adjustment || '0';
      const cleanedValue = adjustment.replace(/[$,]/g, '');
      const price = parseFloat(cleanedValue);
      const quantity = line.quotedQuantity || 1;
      return sum + (isNaN(price) ? 0 : price * quantity);
    }, 0);

    const discount =
      discountCostValue > 0
        ? discountCostValue
        : quoteLines.reduce((sum, line) => {
            const adjustment = line.adjustment || '0';
            const cleanedValue = adjustment.replace(/[$,]/g, '');
            const price = parseFloat(cleanedValue);
            const quantity = line.quotedQuantity || 1;
            const lineTotal = isNaN(price) ? 0 : price * quantity;
            return (
              sum +
              getLineDiscount(
                lineTotal,
                discountPercentValue,
                discountCostValue,
                line.excludeInvoiceDiscount,
              )
            );
          }, 0);
    const afterDiscount = Math.max(0, sub - discount);
    const taxRate = getQuoteDocumentTaxRate(proposal);
    const taxCostFlat = getQuoteDocumentTaxCost(proposal);
    const tax = taxRate > 0 ? afterDiscount * (taxRate / 100) : taxCostFlat;

    return {
      subtotal: sub,
      discountAmount: discount,
      taxAmount: tax,
      total: afterDiscount + tax,
    };
  }, [quoteLines, proposal, discountPercentValue, discountCostValue]);

  const signedDate =
    proposal.signedDate && proposal.signedDate !== NULL_TIME
      ? format(parseISO(proposal.signedDate), 'MMM d, yyyy')
      : null;

  const sentDate =
    proposal.quoteSentDate && proposal.quoteSentDate !== NULL_TIME
      ? format(parseISO(proposal.quoteSentDate), 'MMM d, yyyy')
      : null;

  const viewedDate =
    proposal.customerViewedDate && proposal.customerViewedDate !== NULL_TIME
      ? format(parseISO(proposal.customerViewedDate), 'MMM d, yyyy')
      : null;

  const fullAddress = property
    ? `${property.address}${property.city ? `, ${property.city}` : ''}${property.state ? ` ${property.state}` : ''}${property.zip ? ` ${property.zip}` : ''}`
    : '';

  if (compact) {
    const taxRate = getQuoteDocumentTaxRate(proposal);
    return (
      <div className="flex flex-col gap-3">
        <p className="text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          <RowPill tone={signedDate ? 'success' : sentDate ? 'info' : 'neutral'}>
            {signedDate ? 'Signed' : sentDate ? 'Sent' : 'Draft'}
          </RowPill>
          {[
            sentDate && `Sent ${sentDate}`,
            viewedDate && `viewed ${viewedDate}`,
            signedDate && `signed ${signedDate}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        {isLoadingLines ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : quoteLines.length === 0 ? (
          <p className="text-muted-foreground text-sm">No services on this proposal.</p>
        ) : (
          <>
            <div className="divide-border -mx-4 flex flex-col divide-y border-y">
              {quoteLines.map((line) => {
                const price = parseFloat((line.adjustment || '0').replace(/[$,]/g, ''));
                const quantity = line.quotedQuantity || 1;
                return (
                  <LineItemRow
                    key={line.id}
                    description={line.description || 'Untitled service'}
                    quantity={quantity}
                    unitPrice={isNaN(price) ? 0 : price}
                    discount={getLineDiscount(
                      (isNaN(price) ? 0 : price) * quantity,
                      discountPercentValue,
                      discountCostValue,
                      line.excludeInvoiceDiscount,
                    )}
                    notes={line.quoteStatus === 1 ? ['Approved'] : signedDate ? ['Declined'] : []}
                  />
                );
              })}
            </div>
            <FieldTotals
              subtotal={subtotal}
              discount={{
                amount: discountAmount,
                detail: discountPercentValue > 0 ? `${discountPercentValue}%` : undefined,
              }}
              tax={{ amount: taxAmount, detail: taxRate > 0 ? `${taxRate}%` : undefined }}
              total={total}
            />
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Property Info */}
      {property && (
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="space-y-2">
              <div className="text-sm text-gray-500">Property</div>
              <div className="font-medium">{fullAddress}</div>
              {property.businessname && (
                <div className="text-sm text-gray-600">{property.businessname}</div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status Info */}
      <Card className="bg-white">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-500">Status</div>
              <div className="mt-1">
                {signedDate ? (
                  <Badge className="bg-green-100 text-green-800">Signed</Badge>
                ) : sentDate ? (
                  <Badge className="bg-blue-100 text-blue-800">Delivered</Badge>
                ) : (
                  <Badge className="bg-gray-100 text-gray-800">Draft</Badge>
                )}
              </div>
            </div>
            {sentDate && (
              <div>
                <div className="text-sm text-gray-500">Sent Date</div>
                <div className="mt-1 font-medium">{sentDate}</div>
              </div>
            )}
            {viewedDate && (
              <div>
                <div className="text-sm text-gray-500">Viewed Date</div>
                <div className="mt-1 font-medium">{viewedDate}</div>
              </div>
            )}
            {signedDate && (
              <div>
                <div className="text-sm text-gray-500">Signed Date</div>
                <div className="mt-1 font-medium">{signedDate}</div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Line Items */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold">Services</h3>
        {isLoadingLines ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : quoteLines.length === 0 ? (
          <Card className="bg-white">
            <CardContent className="p-4 text-center text-gray-500">No line items found</CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {quoteLines.map((line) => {
              const adjustment = line.adjustment || '0';
              const cleanedValue = adjustment.replace(/[$,]/g, '');
              const price = parseFloat(cleanedValue);
              const quantity = line.quotedQuantity || 1;
              const lineTotal = price * quantity;
              const lineDiscount = getLineDiscount(
                lineTotal,
                discountPercentValue,
                discountCostValue,
                line.excludeInvoiceDiscount,
              );
              const isApproved = line.quoteStatus === 1;

              return (
                <Card key={line.id} className="bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div
                        className={`mt-1 h-2 w-2 flex-shrink-0 rounded-full ${isApproved ? 'bg-green-500' : 'bg-gray-300'}`}
                        title={isApproved ? 'Approved' : 'Not Approved'}
                      />
                      <div className="flex-1">
                        <div className="font-medium">{line.description || 'Untitled Service'}</div>
                        <div className="mt-1 text-sm text-gray-500">
                          Qty: {quantity} × ${price.toFixed(2)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">${lineTotal.toFixed(2)}</div>
                        {lineDiscount > 0 && (
                          <div className="font-medium text-green-700">
                            Discount -${lineDiscount.toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Totals */}
      {!isLoadingLines && quoteLines.length > 0 && (
        <Card className="border-2 bg-white">
          <CardContent className="p-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium">${subtotal.toFixed(2)}</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    {discountCostValue > 0 ? 'Discount' : 'Total discount'}
                  </span>
                  <span className="font-medium text-red-600">-${discountAmount.toFixed(2)}</span>
                </div>
              )}
              {taxAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    {getQuoteDocumentTaxRate(proposal) > 0
                      ? `Tax (${getQuoteDocumentTaxRate(proposal)}%)`
                      : 'Tax (flat)'}
                  </span>
                  <span className="font-medium">${taxAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between border-t pt-2 text-lg font-bold">
                <span>Total</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
